"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { X, MessageCircle, CircleHelp, Plus, ArrowLeft } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { useCreatePageStore } from "@/stores/createPageStore";
import { hasRenderableChatTranscript } from "@/lib/agent/renderableChatMessage";
import { AgentComposerRecovery, PresetOption } from "@/types/createAgent";
import { CreateFormData } from "@/components/create/types";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  REFERENCE_IMAGE_ACCEPT,
  REFERENCE_IMAGE_FORMAT_LABEL,
} from "@/lib/create/referenceImageUpload";
import {
  describeReferenceImageSelection,
  prepareReferenceImages,
  type PreparedReferenceImage,
  uploadPreparedReferenceImages,
} from "@/lib/create/referenceImageUploadClient";
import { requestVirtualTryOn } from "@/lib/create/virtualTryOnEvents";
import type { DesignerRenderActionId } from "@/lib/agent/designerActionPresentation";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useChatActions } from "../ChatActionsProvider";
import type { DesktopRenderActionState } from "../desktopRenderAction";
import type { MobileFrontReviewIntent } from "../DesktopDressDesignCanvas";
import { getChatPlaceholder } from "../createAgentChat.utils";
import AgentPresets from "../presets/AgentPresets";
import { CommitActionStack } from "../presets/CommitActionStack";
import {
  FrontReviewBackLauncher,
  FrontReviewAmazonLink,
  FrontReviewModifyLauncher,
  FrontReviewPanelBackButton,
} from "../presets/FrontReviewDecision";
import {
  DesignerWorkspaceProvider,
  useDesignerWorkspace,
  RESTORED_DESIGN_MESSAGE_ID,
  restoredDesignerBackWidget,
} from "../DesignerWorkspaceProvider";
import { useChatAutoScroll } from "./useChatAutoScroll";
import ChatComposer from "./ChatComposer";
import ChatErrorBanner from "./ChatErrorBanner";
import ChatGenerationProgress from "./ChatGenerationProgress";
import ChatMessageList from "./ChatMessageList";

const RESTORED_QUOTE_PRESET: PresetOption = {
  label: "Get free quote",
  action: "action",
  actionId: "submit_quote",
  presetType: "commit_action",
  data: {},
  actionData: { shouldCreateTicket: true },
};
const RESTORED_VIRTUAL_TRY_ON_PRESET: PresetOption = {
  label: "Try it on",
  action: "action",
  actionId: "start_virtual_try_on",
  presetType: "commit_action",
  data: {},
};

function referenceOnlyMessage(count: number): string {
  return count === 1
    ? "Use this photo or sketch as inspiration for my dress."
    : "Use these photos or sketches as inspiration for my dress.";
}

function isReferenceOnlyMessage(message: string, imageCount: number): boolean {
  if (imageCount === 0) return false;
  const normalizedMessage = message.trim();
  return (
    normalizedMessage === "Uploaded an image." ||
    normalizedMessage === referenceOnlyMessage(imageCount)
  );
}

interface CreateAgentChatProps {
  /** When provided, clicking an image in a message opens this modal (the one next to Start Over) instead of the message's local modal */
  onOpenFullSizeImage?: (initialView: "front" | "back") => void;
  /** Ref the parent can set and call to reset example-typing state (e.g. when Start Over is confirmed). */
  onStartOverRef?: React.MutableRefObject<(() => void) | null>;
  /** Locks parent-level reset actions while a staged attachment is prepared. */
  onAttachmentPendingChange?: (pending: boolean) => void;
  onOpenConversationRef?: React.MutableRefObject<
    ((intent?: MobileFrontReviewIntent) => void) | null
  >;
  onOpenHelpRef?: React.MutableRefObject<(() => void) | null>;
  /** The canvas renders one attention panel in this slot above the composer. */
  onMobileAttentionHost?: (host: HTMLDivElement | null) => void;
  /** The stable mobile footer destination outside the switchable panels. */
  mobileFooterHost?: HTMLDivElement | null;
  /** Presentation-only first turn while visitor identity is being prepared. */
  optimisticEntryMessage?: {
    id: string;
    message: string;
    imageUrls?: string[];
    createdAt: number;
  };
  /** Exact render capability currently represented by the desktop footer. */
  desktopFooterActionId?: DesignerRenderActionId | null;
  desktopFooterAuthorityPending?: boolean;
  onDesktopRenderActionStateChange?: (state: DesktopRenderActionState) => void;
}

function ChatFooterPlacement({
  children,
  mobileFooterHost,
  sharedWorkspace,
}: {
  children: React.ReactNode;
  mobileFooterHost?: HTMLDivElement | null;
  sharedWorkspace: boolean;
}) {
  const isDesktopWorkspace = useMediaQuery("(min-width: 1024px)");
  if (!sharedWorkspace || isDesktopWorkspace || mobileFooterHost === undefined)
    return <>{children}</>;
  // Hiding the mobile transcript must never hide or recreate its draft.
  return mobileFooterHost ? createPortal(children, mobileFooterHost) : null;
}

export default function CreateAgentChat(props: CreateAgentChatProps) {
  const workspace = useDesignerWorkspace();
  return workspace ? (
    <CreateAgentChatContent {...props} />
  ) : (
    <DesignerWorkspaceProvider sharedWorkspace={false}>
      <CreateAgentChatContent {...props} />
    </DesignerWorkspaceProvider>
  );
}

function CreateAgentChatContent({
  onOpenFullSizeImage,
  onStartOverRef,
  onAttachmentPendingChange,
  onOpenConversationRef,
  onOpenHelpRef,
  onMobileAttentionHost,
  mobileFooterHost,
  optimisticEntryMessage,
  desktopFooterActionId,
  desktopFooterAuthorityPending = false,
  onDesktopRenderActionStateChange,
}: CreateAgentChatProps) {
  const { sendAgentMessage, handleCreateTicket } = useChatActions();
  const workspace = useDesignerWorkspace()!;
  const agent = useCreatePageStore(useShallow(state => state.agent));
  const formData = useCreatePageStore(state => state.form.data);
  const updateFormData = useCreatePageStore(state => state.updateFormData);
  const loading = useCreatePageStore(
    state => state.generation.progress.loading
  );
  const quoteSubmitting = useCreatePageStore(
    state => state.ticket.designSubmitted
  );
  const isModifying = useCreatePageStore(
    state => state.ui.modification.isModifying
  );
  const frontImageUrl = useCreatePageStore(
    state => state.generation.images.front.url
  );
  const backImageUrl = useCreatePageStore(
    state => state.generation.images.back.url
  );
  const messages = agent.conversation.messages;
  const displayMessages = optimisticEntryMessage
    ? [
        ...messages,
        {
          id: optimisticEntryMessage.id,
          role: "user" as const,
          content: optimisticEntryMessage.message,
          timestamp: new Date(optimisticEntryMessage.createdAt),
          imageUrl: optimisticEntryMessage.imageUrls?.[0],
          imageUrls: optimisticEntryMessage.imageUrls,
        },
      ]
    : messages;
  const hasRenderableTranscript = hasRenderableChatTranscript(displayMessages);
  const isTyping = agent.conversation.isTyping;
  const thinkingStatus = agent.conversation.thinkingStatus;
  const thinkingTrace = agent.conversation.thinkingTrace;
  const presetsLoading = agent.conversation.presetsLoading;
  const pendingComposerSeed = agent.pendingComposerSeed;
  const pendingComposerRecovery = agent.pendingComposerRecovery;
  const { visiblePresets } = workspace;
  const [exampleTypingInProgress, setExampleTypingInProgress] = useState(false);
  const [conversationMode, setConversationMode] = useState("chat");
  const [composerIntent, setComposerIntent] = useState<
    "modify" | "back" | null
  >(null);
  useEffect(() => {
    if (!onOpenConversationRef) return;
    onOpenConversationRef.current = intent => {
      setConversationMode("chat");
      if (intent === "modify_front") {
        updateFormData({ frontApproved: false, generateBack: false });
        useCreatePageStore.getState().setPhase("front_review");
        setComposerIntent("modify");
      } else if (intent === "design_back") {
        updateFormData({ frontApproved: true, generateBack: true });
        useCreatePageStore.getState().setPhase("back_design");
        setComposerIntent("back");
      }
    };
    return () => {
      onOpenConversationRef.current = null;
    };
  }, [onOpenConversationRef, updateFormData]);
  useEffect(() => {
    if (!onOpenHelpRef) return;
    onOpenHelpRef.current = () => setConversationMode("qa");
    return () => {
      onOpenHelpRef.current = null;
    };
  }, [onOpenHelpRef]);
  const [inputValue, setInputValue] = useState(() => {
    const initialAgent = useCreatePageStore.getState().agent;
    return (
      initialAgent.pendingComposerRecovery?.composerDraft ??
      (initialAgent.pendingComposerRecovery ||
      initialAgent.pendingSubmittedMessage ||
      !hasRenderableChatTranscript(initialAgent.conversation.messages)
        ? initialAgent.pendingComposerSeed
        : null) ??
      ""
    );
  });
  const [recoveringFailedTurn, setRecoveringFailedTurn] = useState(() => {
    const initialAgent = useCreatePageStore.getState().agent;
    return Boolean(
      initialAgent.pendingComposerRecovery ||
      (initialAgent.pendingComposerSeed &&
        (initialAgent.pendingSubmittedMessage ||
          !hasRenderableChatTranscript(initialAgent.conversation.messages)))
    );
  });
  const [activeComposerRecovery, setActiveComposerRecovery] =
    useState<AgentComposerRecovery | null>(
      () => useCreatePageStore.getState().agent.pendingComposerRecovery ?? null
    );
  const [recoveryImageUrls, setRecoveryImageUrls] = useState<string[]>(() => {
    const recovery =
      useCreatePageStore.getState().agent.pendingComposerRecovery;
    return recovery?.retryImageUrls ?? recovery?.imageUrls ?? [];
  });
  const [recoveryAttachmentStatus, setRecoveryAttachmentStatus] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const [composerReferences, setComposerReferences] = useState<
    PreparedReferenceImage[]
  >([]);
  const [composerReferenceUrls, setComposerReferenceUrls] = useState<string[]>(
    []
  );
  const [composerUploadPending, setComposerUploadPending] = useState(false);
  const [composerUploadError, setComposerUploadError] = useState<string | null>(
    null
  );
  const [composerUploadNotice, setComposerUploadNotice] = useState<
    string | null
  >(null);
  const [composerAttachmentStatus, setComposerAttachmentStatus] = useState("");
  const composerInputEditRevisionRef = React.useRef(0);
  const composerAttachmentEditRevisionRef = React.useRef(0);
  const composerSendPendingRef = React.useRef(false);
  const [composerSendPending, setComposerSendPending] = useState(false);
  const composerUploadInputRef = React.useRef<HTMLInputElement>(null);
  const restoredDesignUrl = frontImageUrl || backImageUrl;
  const restoredDesignView = frontImageUrl ? "front" : "back";
  const isContinuingToBack =
    formData.frontApproved === true || agent.phase === "back_design";

  const setComposerUploadBusy = (pending: boolean) => {
    setComposerUploadPending(pending);
    onAttachmentPendingChange?.(pending);
  };

  useEffect(
    () => () => onAttachmentPendingChange?.(false),
    [onAttachmentPendingChange]
  );

  useEffect(() => {
    // A retry publishes transcript updates before its recovery record settles.
    // Do not replay that record over the draft we just consumed for this send.
    // The send handler restores its snapshot if the retry fails.
    if (composerSendPendingRef.current) return;
    if (pendingComposerRecovery) {
      setActiveComposerRecovery(pendingComposerRecovery);
      setRecoveryImageUrls(
        pendingComposerRecovery.retryImageUrls ??
          pendingComposerRecovery.imageUrls
      );
      setRecoveringFailedTurn(true);
    }
    const canRestoreSeed =
      Boolean(agent.pendingSubmittedMessage) || !hasRenderableTranscript;
    const visibleRecoveryDraft =
      pendingComposerRecovery?.composerDraft ??
      (pendingComposerRecovery || canRestoreSeed ? pendingComposerSeed : null);
    if (visibleRecoveryDraft === null || visibleRecoveryDraft === undefined) {
      if (pendingComposerSeed && !pendingComposerRecovery && !canRestoreSeed) {
        // A bare seed from an older restored prompt is not a pending send once
        // this conversation already has visible turns. Do not put it back into
        // the composer after the customer has moved on.
        setInputValue(current =>
          composerInputEditRevisionRef.current === 0 &&
          current.trim() === pendingComposerSeed.trim()
            ? ""
            : current
        );
        setRecoveringFailedTurn(false);
        useCreatePageStore.getState().clearPendingComposerSeed();
      }
      return;
    }
    // Auth recovery can finish after the chat shell is already mounted. Put a
    // failed automatic resume back into the composer without overwriting text
    // the customer has started typing in the meantime.
    setInputValue(current => {
      if (pendingComposerRecovery?.composerDraft !== undefined) {
        const normalizedCurrent = current.trim();
        const currentIsRoutingCopy =
          normalizedCurrent === pendingComposerRecovery.message.trim() ||
          normalizedCurrent === pendingComposerSeed?.trim();
        return !normalizedCurrent || currentIsRoutingCopy
          ? pendingComposerRecovery.composerDraft
          : current;
      }
      return current.trim() ? current : visibleRecoveryDraft;
    });
    setRecoveringFailedTurn(true);
    if (pendingComposerRecovery || !pendingComposerSeed) return;
    const failedUserTurn = [...messages]
      .reverse()
      .find(
        message =>
          message.role === "user" &&
          message.content.trim() === pendingComposerSeed.trim()
      );
    const failedImageUrls = failedUserTurn?.imageUrls?.length
      ? failedUserTurn.imageUrls
      : failedUserTurn?.imageUrl
        ? [failedUserTurn.imageUrl]
        : [];
    setRecoveryImageUrls(failedImageUrls);
  }, [
    agent.pendingSubmittedMessage,
    composerSendPending,
    hasRenderableTranscript,
    messages,
    pendingComposerRecovery,
    pendingComposerSeed,
  ]);

  if (onStartOverRef) {
    // Intentional render-time assignment (unchanged from before the chat
    // split): the parent only invokes this from event handlers, never
    // during render.
    onStartOverRef.current = () => setExampleTypingInProgress(false);
  }

  const syncMessageToFormState = (
    message: string,
    imageUrl?: string,
    imageUrls?: string[]
  ) => {
    const trimmedMessage = message.trim();
    const imageCount = imageUrls?.length ?? (imageUrl ? 1 : 0);
    const isImageOnlyMessage = isReferenceOnlyMessage(
      trimmedMessage,
      imageCount
    );

    if (
      isImageOnlyMessage ||
      trimmedMessage.length < 3 ||
      agent.designBrief ||
      frontImageUrl ||
      backImageUrl
    ) {
      return;
    }

    const updates: Partial<CreateFormData> = {};
    const isFrontApproved = formData.frontApproved === true;

    if (agent.phase === "back_design" || agent.phase === "back_review") {
      if (!formData.backPrompt?.trim()) {
        updates.backPrompt = trimmedMessage;
      }
    } else if (
      agent.phase === "initial" ||
      agent.phase === "front_design" ||
      agent.phase === "front_review"
    ) {
      if (!isFrontApproved && !formData.prompt?.trim()) {
        updates.prompt = trimmedMessage;
      }
    } else if (!isFrontApproved && !formData.prompt?.trim()) {
      updates.prompt = trimmedMessage;
    }

    if (Object.keys(updates).length > 0) {
      updateFormData(updates);
    }
  };

  const {
    authLoading,
    processingPresetId,
    selectedPresetLabelsByMessageId,
    handlePresetClick,
    handlePresetSelect,
    handleSelectedPresetGenerate,
    handleSelectedPresetChatMore,
    handleRestoredQuote,
    handleRestoredPresetGenerate,
  } = workspace.interaction;

  const { containerRef: messagesContainerRef, contentRef: messagesContentRef } =
    useChatAutoScroll({
      isStreaming:
        isTyping || presetsLoading || Boolean(optimisticEntryMessage),
      isGenerating: loading || isModifying,
      messageCount: displayMessages.length,
      lastMessageRole: displayMessages[displayMessages.length - 1]?.role,
    });

  const handleSendMessage = async (message: string) => {
    if (
      useCreatePageStore.getState().ticket.designSubmitted ||
      composerSendPendingRef.current
    )
      return;
    composerSendPendingRef.current = true;
    setComposerSendPending(true);
    const submittedComposerReferences = composerReferences;
    const submittedComposerReferenceUrls = composerReferenceUrls;
    const submittedComposerAttachmentStatus = composerAttachmentStatus;
    const submittedComposerUploadNotice = composerUploadNotice;
    const submittedRecoveryImageUrls = recoveryImageUrls;
    const submittedRecoveryAttachmentStatus = recoveryAttachmentStatus;
    const submittedInputEditRevision = composerInputEditRevisionRef.current;
    const submittedAttachmentEditRevision =
      composerAttachmentEditRevisionRef.current;

    setExampleTypingInProgress(false);
    setRecoveryNotice("");
    setComposerUploadError(null);
    setComposerUploadNotice(null);
    useCreatePageStore.getState().clearPendingComposerSeed();
    const composerDataUrls = recoveringFailedTurn
      ? []
      : submittedComposerReferences.map(reference => reference.dataUrl);
    const needsComposerUpload =
      composerDataUrls.length > 0 &&
      submittedComposerReferenceUrls.length !== composerDataUrls.length;
    if (needsComposerUpload) {
      setComposerUploadBusy(true);
    }
    const retryImageUrls = recoveringFailedTurn
      ? submittedRecoveryImageUrls
      : [];
    const recoveryContext = recoveringFailedTurn
      ? activeComposerRecovery
      : null;

    // Consume the visible draft as soon as the submit is accepted. The exact
    // text and attachments above remain available for a lossless rollback if
    // upload, credit gating, transport, or streaming completion fails.
    setInputValue("");
    if (recoveringFailedTurn) {
      setRecoveryImageUrls([]);
      setRecoveryAttachmentStatus("");
    } else {
      setComposerReferences([]);
      setComposerReferenceUrls([]);
      setComposerAttachmentStatus("");
    }

    let composerImageUrls = submittedComposerReferenceUrls;
    try {
      let hostedComposerUrls: string[] | null = null;
      if (needsComposerUpload) {
        try {
          hostedComposerUrls = await uploadPreparedReferenceImages(
            composerDataUrls,
            agent.phase === "back_design" || agent.phase === "back_review"
              ? "back"
              : "front"
          );
        } finally {
          // This flag describes attachment preparation, not the lifetime of
          // the chat response. Releasing it here lets the customer stage the
          // next reference without an older send later unlocking that work.
          setComposerUploadBusy(false);
        }
      }
      composerImageUrls = needsComposerUpload
        ? (hostedComposerUrls ?? composerDataUrls)
        : submittedComposerReferenceUrls;
      const submittedImageUrls =
        retryImageUrls.length > 0 ? retryImageUrls : composerImageUrls;
      const isComposerImageOnly =
        message.trim().length === 0 && submittedImageUrls.length > 0;
      const submittedMessage =
        message.trim() ||
        (isComposerImageOnly
          ? referenceOnlyMessage(submittedImageUrls.length)
          : message);
      const recoveryReferencesRemoved = Boolean(
        recoveryContext?.imageUrls.length && retryImageUrls.length === 0
      );
      const unchangedRecoveryMessage = Boolean(
        recoveryContext &&
        submittedMessage.trim() === recoveryContext.message.trim()
      );
      const referenceOnlyRecovery = Boolean(
        recoveryContext?.imageUrls.length &&
        recoveryContext.formStateOverride?.prompt === ""
      );
      const retryFormStateOverride = recoveryContext?.formStateOverride
        ? {
            ...recoveryContext.formStateOverride,
            prompt:
              referenceOnlyRecovery &&
              unchangedRecoveryMessage &&
              !recoveryReferencesRemoved
                ? ""
                : submittedMessage.trim(),
            ...(recoveryReferencesRemoved && {
              uploadedFrontImages: [],
              uploadedFrontImageUrls: [],
              uploadedFrontImageCount: 0,
              isUploadPreset: false,
              isFrontUploadPreset: false,
              preset_type: "",
            }),
          }
        : undefined;

      if (retryFormStateOverride) {
        updateFormData(retryFormStateOverride);
      } else if (!isComposerImageOnly) {
        syncMessageToFormState(
          submittedMessage,
          submittedImageUrls[0],
          submittedImageUrls.length > 0 ? submittedImageUrls : undefined
        );
      }

      if (recoveringFailedTurn) {
        await sendAgentMessage(
          submittedMessage,
          retryImageUrls[0],
          retryImageUrls.length > 0 ? retryImageUrls : undefined,
          {
            ...(composerIntent === "modify" && {
              composerMode: "modify_front" as const,
            }),
            recoverComposerOnFailure: true,
            ...(message !== submittedMessage && { composerDraft: message }),
            ...(recoveryContext && {
              composerRecovery: recoveryContext,
            }),
            ...(retryFormStateOverride && {
              formStateOverride: retryFormStateOverride,
            }),
            ...((recoveryReferencesRemoved
              ? "custom_prompt"
              : recoveryContext?.entryPoint) && {
              entryPoint: recoveryReferencesRemoved
                ? ("custom_prompt" as const)
                : recoveryContext?.entryPoint,
            }),
          }
        );
      } else if (composerIntent === "modify" && !isComposerImageOnly) {
        await sendAgentMessage(
          submittedMessage,
          composerImageUrls[0],
          composerImageUrls.length > 0 ? composerImageUrls : undefined,
          {
            composerMode: "modify_front",
            recoverComposerOnFailure: true,
            ...(message !== submittedMessage && { composerDraft: message }),
          }
        );
      } else {
        await sendAgentMessage(
          submittedMessage,
          composerImageUrls[0],
          composerImageUrls.length > 0 ? composerImageUrls : undefined,
          {
            recoverComposerOnFailure: true,
            ...(message !== submittedMessage && { composerDraft: message }),
          }
        );
      }
      const settledAgentState = useCreatePageStore.getState().agent;
      const recoveryWasReconciled = Boolean(
        recoveryContext && settledAgentState.pendingComposerRecovery === null
      );
      const reconciledDraftWasQueued = Boolean(
        settledAgentState.pendingComposerSeed?.trim()
      );
      if (
        recoveryWasReconciled &&
        (reconciledDraftWasQueued ||
          !unchangedRecoveryMessage ||
          recoveryReferencesRemoved)
      ) {
        // The server already owned the original uncertain turn. Keep the
        // customer's newer draft as a normal next message instead of silently
        // discarding it or appending it against stale local history.
        if (
          composerInputEditRevisionRef.current === submittedInputEditRevision
        ) {
          setInputValue(current =>
            current.trim()
              ? current
              : (settledAgentState.pendingComposerSeed ?? message)
          );
        }
        setComposerIntent(null);
        setRecoveringFailedTurn(false);
        setActiveComposerRecovery(null);
        setRecoveryImageUrls([]);
        setRecoveryAttachmentStatus("");
        useCreatePageStore.getState().clearPendingComposerSeed();
        setRecoveryNotice(
          "Your previous message was restored. Your new message is ready to send."
        );
        return;
      }

      setComposerIntent(null);
      setRecoveringFailedTurn(false);
      setActiveComposerRecovery(null);
      setRecoveryImageUrls([]);
      setRecoveryAttachmentStatus("");
      useCreatePageStore.getState().setPendingComposerRecovery(null);
    } catch {
      // The transport hook owns the banner and durable retry record. Restore
      // the local snapshot too so even a synchronous caller rejection cannot
      // erase the draft while that store recovery propagates through React.
      if (composerInputEditRevisionRef.current === submittedInputEditRevision) {
        setInputValue(current =>
          !current.trim() || current.trim() === message.trim()
            ? message
            : current
        );
      }
      if (
        composerAttachmentEditRevisionRef.current ===
        submittedAttachmentEditRevision
      ) {
        if (recoveringFailedTurn) {
          setRecoveryImageUrls(current =>
            current.length > 0 ? current : submittedRecoveryImageUrls
          );
          setRecoveryAttachmentStatus(current =>
            current ? current : submittedRecoveryAttachmentStatus
          );
        } else {
          setComposerReferences(current =>
            current.length > 0 ? current : submittedComposerReferences
          );
          setComposerReferenceUrls(current =>
            current.length > 0 ? current : composerImageUrls
          );
          setComposerAttachmentStatus(current =>
            current ? current : submittedComposerAttachmentStatus
          );
          setComposerUploadNotice(
            current => current ?? submittedComposerUploadNotice
          );
        }
      }
    } finally {
      composerSendPendingRef.current = false;
      setComposerSendPending(false);
    }
  };

  const handleComposerUploadImages = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0 || composerUploadPending) return;

    composerAttachmentEditRevisionRef.current += 1;
    setComposerUploadError(null);
    setComposerUploadNotice(null);
    setComposerUploadBusy(true);
    try {
      const { entries, rejectedCount } = await prepareReferenceImages(files);
      if (entries.length === 0) {
        setComposerUploadError(
          `Choose ${REFERENCE_IMAGE_FORMAT_LABEL} up to 5 MB, or try a smaller screenshot.`
        );
        return;
      }
      setComposerReferences(entries);
      setComposerReferenceUrls([]);
      const partialNotice = describeReferenceImageSelection(
        entries.length,
        rejectedCount
      );
      setComposerUploadNotice(partialNotice);
      setComposerAttachmentStatus(
        partialNotice ??
          (entries.length === 1
            ? "1 inspiration photo attached."
            : `${entries.length} inspiration photos attached.`)
      );
    } catch {
      setComposerUploadError(
        "We couldn't read that image. Please try another file."
      );
    } finally {
      setComposerUploadBusy(false);
    }
  };

  const handleRemoveComposerImages = () => {
    const removedCount = composerReferences.length;
    composerAttachmentEditRevisionRef.current += 1;
    setComposerReferences([]);
    setComposerReferenceUrls([]);
    setComposerUploadError(null);
    setComposerUploadNotice(null);
    setComposerAttachmentStatus(
      removedCount === 1
        ? "Inspiration photo removed."
        : "Inspiration photos removed."
    );
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLTextAreaElement>(
          'textarea[aria-label="Chat message input"]'
        )
        ?.focus({ preventScroll: true });
    });
  };

  const handleRemoveRecoveryImages = () => {
    composerAttachmentEditRevisionRef.current += 1;
    const recoveryImageCount = activeComposerRecovery?.imageUrls.length ?? 0;
    const removesReferenceOnlyPrompt = Boolean(
      recoveryImageCount > 0 &&
      inputValue.trim() === activeComposerRecovery?.message.trim() &&
      (activeComposerRecovery?.formStateOverride?.prompt === "" ||
        isReferenceOnlyMessage(inputValue, recoveryImageCount))
    );
    const removedReferenceFormState = activeComposerRecovery?.formStateOverride
      ? {
          ...activeComposerRecovery.formStateOverride,
          prompt: removesReferenceOnlyPrompt ? "" : inputValue.trim(),
          uploadedFrontImages: [],
          uploadedFrontImageUrls: [],
          uploadedFrontImageCount: 0,
          isUploadPreset: false,
          isFrontUploadPreset: false,
          preset_type: "",
        }
      : undefined;
    const updatedRecovery = activeComposerRecovery
      ? {
          ...activeComposerRecovery,
          retryImageUrls: [],
          entryPoint: "custom_prompt" as const,
          ...(removedReferenceFormState && {
            formStateOverride: removedReferenceFormState,
          }),
        }
      : null;
    setRecoveryImageUrls([]);
    setActiveComposerRecovery(updatedRecovery);
    if (removedReferenceFormState) {
      updateFormData(removedReferenceFormState);
    }
    if (updatedRecovery) {
      useCreatePageStore.getState().setPendingComposerRecovery(updatedRecovery);
    }
    if (removesReferenceOnlyPrompt) {
      setInputValue("");
      useCreatePageStore.getState().clearPendingComposerSeed();
    } else if (inputValue.trim()) {
      useCreatePageStore.getState().setPendingComposerSeed(inputValue);
    }
    setRecoveryAttachmentStatus(
      activeComposerRecovery?.imageUrls.length === 1
        ? "Retry inspiration photo removed."
        : "Retry inspiration photos removed."
    );
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLTextAreaElement>(
          'textarea[aria-label="Chat message input"]'
        )
        ?.focus({ preventScroll: true });
    });
  };

  const handleComposerModifyRequest = () => {
    setComposerIntent("modify");
    const input = document.querySelector<HTMLTextAreaElement>(
      'textarea[aria-label="Chat message input"]'
    );
    input?.focus({ preventScroll: true });
    input?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  };

  const handleComposerModifyExit = () => {
    setComposerIntent(null);
  };

  const handleRestoredBackRequest = () => {
    setComposerIntent("back");
    updateFormData({ frontApproved: true, generateBack: true });
    useCreatePageStore.getState().setPhase("back_design");
  };

  const handleRestoredDecisionBack = () => {
    setComposerIntent(null);
    updateFormData({ frontApproved: false, generateBack: false });
    useCreatePageStore.getState().setPhase("front_review");
  };

  const handleQuoteRetry = () => {
    void handleCreateTicket();
  };

  const handleRestoredModifyBack = () => {
    setComposerIntent(null);
  };

  const restoredBackWidget = restoredDesignerBackWidget(formData.prompt);
  const restoredDecisionDisabled =
    quoteSubmitting ||
    authLoading ||
    loading ||
    isModifying ||
    isTyping ||
    Boolean(processingPresetId);

  return (
    <Tabs
      value={conversationMode}
      onValueChange={setConversationMode}
      className="flex flex-col flex-1 min-h-0 min-w-0 gap-0 overflow-hidden"
    >
      {workspace.sharedWorkspace ? (
        <div className="designer-chat-header">
          <h2>Your designer</h2>
          <TabsList
            aria-label="Conversation mode"
            className="designer-conversation-tabs h-auto w-auto"
          >
            <TabsTrigger
              value="chat"
              className="designer-tab h-11 rounded-xl gap-3"
            >
              <MessageCircle className="size-[18px]" aria-hidden />
              Chat
            </TabsTrigger>
            <TabsTrigger
              value="qa"
              className="designer-tab h-11 rounded-xl gap-3"
            >
              <CircleHelp className="size-[18px]" aria-hidden />
              Q&A
            </TabsTrigger>
          </TabsList>
        </div>
      ) : null}
      <input
        ref={composerUploadInputRef}
        type="file"
        accept={REFERENCE_IMAGE_ACCEPT}
        multiple
        hidden
        tabIndex={-1}
        data-testid="chat-composer-upload-input"
        onChange={handleComposerUploadImages}
      />
      {/* Messages area - native scroll container. `overscroll-contain` stops
          scroll chaining to the document (avoids pulling the sticky navbar out
          of view on iOS). Using a plain div instead of Radix ScrollArea keeps
          iOS momentum scroll + focus-scroll-into-view behaving natively. */}
      <TabsContent
        value="chat"
        forceMount
        className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
      >
        <Card
          variant="chat-messages"
          padding="none"
          className="flex-1 min-h-0 overflow-hidden flex flex-col min-w-0"
        >
          <div
            ref={messagesContainerRef}
            className="min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div
              ref={messagesContentRef}
              data-slot="chat-transcript-content"
              className="designer-transcript"
            >
              {!hasRenderableTranscript && restoredDesignUrl && (
                <div
                  data-restored-design-decision
                  className="rounded-xl border border-border bg-card/80 p-3 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => onOpenFullSizeImage?.(restoredDesignView)}
                      className="relative h-28 w-20 shrink-0 overflow-hidden rounded-lg border border-border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`View saved ${restoredDesignView} design`}
                    >
                      <Image
                        src={restoredDesignUrl}
                        alt={`Saved ${restoredDesignView} dress design`}
                        fill
                        sizes="80px"
                        className="object-cover object-top"
                      />
                    </button>
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        {isContinuingToBack
                          ? "Front design ready"
                          : composerIntent === "modify"
                            ? "Describe one change to the front"
                            : "Your front design"}
                      </p>
                      <p className="text-sm leading-5 text-muted-foreground">
                        {isContinuingToBack
                          ? "Choose one direction for the back."
                          : composerIntent === "modify"
                            ? "Use the message box below."
                            : "What would you like to do next?"}
                      </p>
                    </div>
                  </div>
                  {!isContinuingToBack && composerIntent !== "modify" ? (
                    <div className="mt-3 border-t border-border/70 pt-3">
                      <CommitActionStack
                        presets={[
                          RESTORED_QUOTE_PRESET,
                          RESTORED_VIRTUAL_TRY_ON_PRESET,
                        ]}
                        presetClassName={() =>
                          workspace.sharedWorkspace ? "hidden" : undefined
                        }
                        getPresetState={preset => ({
                          isProcessing:
                            processingPresetId ===
                            `${preset.actionId}-${preset.label}`,
                          isDisabled: restoredDecisionDisabled,
                          isSelected: false,
                        })}
                        onPresetClick={preset => {
                          if (preset.actionId === "start_virtual_try_on") {
                            requestVirtualTryOn();
                            return;
                          }
                          void handleRestoredQuote();
                        }}
                        supportChildren={[
                          <FrontReviewAmazonLink key="amazon" />,
                          <FrontReviewModifyLauncher
                            key="modify-front"
                            disabled={restoredDecisionDisabled}
                            onClick={handleComposerModifyRequest}
                          />,
                          <FrontReviewBackLauncher
                            key="design-back"
                            disabled={restoredDecisionDisabled}
                            onClick={handleRestoredBackRequest}
                          />,
                        ]}
                      />
                    </div>
                  ) : composerIntent === "modify" ? (
                    <div className="mt-3 border-t border-border/70 pt-2">
                      <FrontReviewPanelBackButton
                        panel="modify"
                        autoFocus={false}
                        disabled={restoredDecisionDisabled}
                        onClick={handleRestoredModifyBack}
                      />
                    </div>
                  ) : (
                    <div className="mt-3 grid gap-2 border-t border-border/70 pt-2">
                      <FrontReviewPanelBackButton
                        panel="back"
                        autoFocus={false}
                        disabled={restoredDecisionDisabled}
                        onClick={handleRestoredDecisionBack}
                      />
                      {restoredBackWidget ? (
                        <AgentPresets
                          messageId={RESTORED_DESIGN_MESSAGE_ID}
                          presets={[]}
                          widgets={[restoredBackWidget]}
                          disabled={restoredDecisionDisabled}
                          processingPresetId={processingPresetId}
                          selectedPresetLabel={
                            selectedPresetLabelsByMessageId[
                              RESTORED_DESIGN_MESSAGE_ID
                            ] ?? null
                          }
                          onPresetClick={(preset, source) =>
                            handlePresetSelect(
                              RESTORED_DESIGN_MESSAGE_ID,
                              preset,
                              source
                            )
                          }
                          onPresetSelect={(preset, source) =>
                            handlePresetSelect(
                              RESTORED_DESIGN_MESSAGE_ID,
                              preset,
                              source
                            )
                          }
                          onPresetGenerate={preset => {
                            if (restoredDecisionDisabled) return;
                            void handleRestoredPresetGenerate(
                              RESTORED_DESIGN_MESSAGE_ID,
                              preset
                            );
                          }}
                          onPresetChatMore={preset => {
                            if (!preset) return;
                            handlePresetSelect(
                              RESTORED_DESIGN_MESSAGE_ID,
                              preset
                            );
                            void sendAgentMessage(
                              `Help me explore this back direction: ${preset.label}`
                            );
                          }}
                          desktopFooterActionId={desktopFooterActionId}
                          desktopFooterAuthorityPending={
                            desktopFooterAuthorityPending
                          }
                          onDesktopRenderActionStateChange={
                            onDesktopRenderActionStateChange
                          }
                        />
                      ) : (
                        <p className="px-1 py-2 text-sm text-muted-foreground">
                          Describe the back you want in the message box below.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <ChatMessageList
                messages={displayMessages}
                isTyping={isTyping || Boolean(optimisticEntryMessage)}
                thinkingStatus={
                  optimisticEntryMessage
                    ? "Getting your designer ready"
                    : thinkingStatus
                }
                thinkingTrace={thinkingTrace}
                presetsLoading={presetsLoading}
                loading={loading}
                actionsDisabled={quoteSubmitting}
                isModifying={isModifying}
                exampleTypingInProgress={exampleTypingInProgress}
                visiblePresets={visiblePresets}
                authLoading={authLoading}
                processingPresetId={processingPresetId}
                selectedPresetLabelsByMessageId={
                  selectedPresetLabelsByMessageId
                }
                desktopFooterActionId={desktopFooterActionId}
                desktopFooterAuthorityPending={desktopFooterAuthorityPending}
                onDesktopRenderActionStateChange={
                  onDesktopRenderActionStateChange
                }
                onOpenFullSizeImage={onOpenFullSizeImage}
                onPresetClick={handlePresetClick}
                onPresetSelect={handlePresetSelect}
                onPresetGenerate={handleSelectedPresetGenerate}
                onPresetChatMore={handleSelectedPresetChatMore}
                onComposerModifyRequest={handleComposerModifyRequest}
                onComposerModifyExit={handleComposerModifyExit}
              />
              {conversationMode === "chat" ? (
                <ChatErrorBanner onQuoteRetry={handleQuoteRetry} />
              ) : null}
              {!workspace.sharedWorkspace && <ChatGenerationProgress />}
            </div>
          </div>
        </Card>
      </TabsContent>
      <TabsContent
        value="qa"
        className="min-h-0 flex-1 overflow-y-auto px-4 py-6 lg:px-6"
      >
        {workspace.sharedWorkspace ? (
          <button
            type="button"
            className="designer-control mb-3 flex min-h-11 items-center gap-2 rounded-xl px-2 text-sm lg:hidden"
            onClick={() => {
              setConversationMode("chat");
              document.getElementById("designer-mobile-tab-chat")?.focus();
            }}
          >
            <ArrowLeft className="size-4" aria-hidden="true" /> Back to chat
          </button>
        ) : null}
        <div className="mb-4 space-y-3 empty:hidden">
          <ChatErrorBanner onQuoteRetry={handleQuoteRetry} />
        </div>
        <h2 className="text-xl font-semibold leading-7">
          Help with your design
        </h2>
        <p className="mt-3 text-sm leading-5">
          Ask about fit, fabric, or your next step.
        </p>
        <div className="mt-4 space-y-3">
          {[
            [
              "How do previews work?",
              frontImageUrl || backImageUrl
                ? "Edits made after a preview stay pending until you apply changes. Your saved preview stays visible while the update is created."
                : "Generate preview creates the front view from your design details. You can keep editing until you choose to generate it. The button shows the credit cost.",
            ],
            [
              "Can I change just the front?",
              frontImageUrl || backImageUrl
                ? "Yes. Tell the designer which view you want to change. Apply changes shows the affected views and total credits before you continue."
                : "Yes. Tell the designer what you want to change on the front. You can refine the design before generating a preview.",
            ],
            [
              "What photo works for try-on?",
              "Use a clear, full-body photo with one person facing the camera. Even lighting and a simple background help.",
            ],
          ].map(([question, answer], index) => (
            <details
              key={question}
              open={index === 0 ? true : undefined}
              className="rounded-xl border border-border p-3.5 open:bg-accent"
            >
              <summary className="cursor-pointer list-none text-sm font-medium [&::-webkit-details-marker]:hidden">
                {question}
              </summary>
              <p className="mt-2.5 text-[13px] leading-5">{answer}</p>
            </details>
          ))}
        </div>
      </TabsContent>

      <ChatFooterPlacement
        mobileFooterHost={mobileFooterHost}
        sharedWorkspace={Boolean(workspace.sharedWorkspace)}
      >
        {recoveryNotice ? (
          <p
            role="status"
            aria-live="polite"
            className="px-1 pb-1 text-xs leading-snug text-muted-foreground"
          >
            {recoveryNotice}
          </p>
        ) : null}
        {composerUploadPending ? (
          <p
            role="status"
            aria-live="polite"
            className="px-4 pb-1 text-xs text-muted-foreground sm:px-6"
          >
            Preparing inspiration photos…
          </p>
        ) : null}
        {composerUploadError ? (
          <p
            role="alert"
            className="px-4 pb-1 text-xs leading-snug text-destructive sm:px-6"
          >
            {composerUploadError}
          </p>
        ) : null}
        {composerUploadNotice ? (
          <p
            role="status"
            className="px-4 pb-1 text-xs leading-snug text-muted-foreground sm:px-6"
          >
            {composerUploadNotice}
          </p>
        ) : null}
        {workspace.sharedWorkspace ? (
          <div
            ref={onMobileAttentionHost}
            data-slot="mobile-attention-host"
            className="shrink-0 lg:hidden"
          />
        ) : null}
        <ChatComposer
          useBottomInset={!workspace.sharedWorkspace}
          onSendMessage={message => {
            setConversationMode("chat");
            void handleSendMessage(message);
          }}
          disabled={
            loading ||
            quoteSubmitting ||
            isModifying ||
            !!processingPresetId ||
            composerUploadPending ||
            Boolean(optimisticEntryMessage)
          }
          isTyping={
            isTyping || presetsLoading || Boolean(optimisticEntryMessage)
          }
          placeholder={
            conversationMode === "qa"
              ? "Ask a question…"
              : composerIntent === "modify"
                ? "Describe one change to the front…"
                : composerIntent === "back"
                  ? "Describe the back of your dress…"
                  : getChatPlaceholder(agent.phase)
          }
          value={inputValue}
          onValueChange={value => {
            composerInputEditRevisionRef.current += 1;
            setInputValue(value);
          }}
          canSendWithoutMessage={
            recoveryImageUrls.length > 0 || composerReferences.length > 0
          }
          startAction={
            recoveryImageUrls.length > 0 ? (
              <button
                type="button"
                className="relative size-14 shrink-0 touch-manipulation overflow-hidden rounded-2xl border border-border bg-muted shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={handleRemoveRecoveryImages}
                aria-label={
                  recoveryImageUrls.length === 1
                    ? "Remove retry inspiration photo"
                    : `Remove ${recoveryImageUrls.length} retry inspiration photos`
                }
              >
                <Image
                  src={recoveryImageUrls[0]}
                  alt=""
                  fill
                  sizes="56px"
                  unoptimized
                  className="object-cover"
                />
                {recoveryImageUrls.length > 1 ? (
                  <span className="absolute inset-x-0 bottom-0 bg-black/70 py-0.5 text-center text-[10px] font-semibold text-white">
                    +{recoveryImageUrls.length - 1}
                  </span>
                ) : null}
                <span className="absolute right-0.5 top-0.5 grid size-4 place-items-center rounded-full bg-black/70 text-white">
                  <X className="size-2.5" aria-hidden="true" />
                </span>
              </button>
            ) : composerReferences.length > 0 ? (
              <button
                type="button"
                className="relative size-14 shrink-0 touch-manipulation overflow-hidden rounded-2xl border border-border bg-muted shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={handleRemoveComposerImages}
                disabled={composerUploadPending}
                aria-label={
                  composerReferences.length === 1
                    ? "Remove inspiration photo"
                    : `Remove ${composerReferences.length} inspiration photos`
                }
              >
                <Image
                  src={composerReferences[0].dataUrl}
                  alt=""
                  fill
                  sizes="56px"
                  unoptimized
                  className="object-cover"
                />
                {composerReferences.length > 1 ? (
                  <span className="absolute inset-x-0 bottom-0 bg-black/70 py-0.5 text-center text-[10px] font-semibold text-white">
                    +{composerReferences.length - 1}
                  </span>
                ) : null}
                <span className="absolute right-0.5 top-0.5 grid size-4 place-items-center rounded-full bg-black/70 text-white">
                  <X className="size-2.5" aria-hidden="true" />
                </span>
              </button>
            ) : !recoveringFailedTurn ? (
              <button
                type="button"
                className="grid size-14 shrink-0 touch-manipulation place-items-center rounded-2xl border border-dashed border-border bg-background text-brand-logo shadow-sm transition-colors hover:border-brand-logo/50 hover:bg-brand-logo/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-50"
                onClick={() => composerUploadInputRef.current?.click()}
                disabled={
                  quoteSubmitting ||
                  loading ||
                  isModifying ||
                  !!processingPresetId ||
                  composerUploadPending ||
                  Boolean(optimisticEntryMessage)
                }
                aria-label="Add up to 3 inspiration photos or sketches"
                aria-busy={composerUploadPending || undefined}
              >
                <Plus className="size-5" aria-hidden="true" />
              </button>
            ) : undefined
          }
        />
        <p role="status" aria-live="polite" className="sr-only">
          {recoveryAttachmentStatus ||
            (composerUploadNotice ? "" : composerAttachmentStatus)}
        </p>
      </ChatFooterPlacement>
    </Tabs>
  );
}
